import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Badge, Box, Button, Divider, Flex, HStack, Icon, IconButton,
  Input, InputGroup, InputLeftElement, Popover, PopoverBody,
  PopoverContent, PopoverTrigger, Radio, RadioGroup, Text,
  VStack, useColorModeValue, useDisclosure, useToast,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { ShieldCheck, Users, SlidersHorizontal } from "lucide-react";
import { OnflyLoading } from "../../../tools/OnflyLoading";
import { getWhitelistEntries, addWhitelistEntry, removeWhitelistEntry, patchWhitelistEntry, setWhitelistCandidateEmail, resendWelcomeEmail } from "../../../lib/api";
import type { WhitelistEntry } from "../../../types/whitelist";
import { EditModal } from "./components/EditModal";
import { SetEmailModal } from "./components/SetEmailModal";
import { AddCandidateForm, type AddCandidateFormData } from "./components/AddCandidateForm";
import { WhitelistEntryRow } from "./components/WhitelistEntryRow";
import { WhitelistPagination } from "./components/WhitelistPagination";



// ---------------------------------------------------------------------------
// WhitelistSection
// ---------------------------------------------------------------------------
interface WhitelistSectionProps {
  token: string;
  filterTag?: "all" | "pj" | "tech";
  sortOrder?: "newest" | "oldest";
}

type FilterTag  = "all" | "pj" | "tech";
type SortOrder  = "newest" | "oldest";

export const WhitelistSection: React.FC<WhitelistSectionProps> = ({ token, filterTag: externalFilterTag, sortOrder: externalSortOrder }) => {
  const [entries, setEntries]           = useState<WhitelistEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch]             = useState("");
  const [filterTagInternal, setFilterTag] = useState<FilterTag>("all");
  const [sortOrderInternal, setSortOrder] = useState<SortOrder>("newest");
  const filterTag = externalFilterTag ?? filterTagInternal;
  const sortOrder = externalSortOrder ?? sortOrderInternal;
  const hasExternalFilters = externalFilterTag !== undefined || externalSortOrder !== undefined;
  const [currentPage, setCurrentPage]   = useState(1);
  const [pageSize, setPageSize]         = useState(5);
  const [isLoading, setIsLoading]       = useState(false);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<WhitelistEntry | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();

  const [setEmailEntry, setSetEmailEntry] = useState<WhitelistEntry | null>(null);
  const [isSavingEmail, setIsSavingEmail] = useState(false);
  const { isOpen: isSetEmailOpen, onOpen: onSetEmailOpen, onClose: onSetEmailClose } = useDisclosure();

  const toast = useToast();

  const cardBg      = useColorModeValue("white", "#0f1f3a");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.100");
  const inputBg     = useColorModeValue("gray.50", "#0a1628");
  const labelColor  = useColorModeValue("gray.500", "gray.400");
  const sectionBg   = useColorModeValue("gray.50", "#0a1628");

  const fetchWhitelist = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWhitelistEntries(token);
      setEntries(data.entries);
    } catch (error: any) {
      toast({ title: "Erro ao carregar whitelist", description: error.message ?? "Erro desconhecido", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { if (token) fetchWhitelist(); }, [fetchWhitelist]);

  useEffect(() => { setCurrentPage(1); }, [search, filterTag, sortOrder, pageSize]);

  const filtered = useMemo(() => {
    let result = [...entries];
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter((e) =>
        e.email.toLowerCase().includes(term) ||
        (e.candidate_full_name ?? "").toLowerCase().includes(term)
      );
    }
    if (filterTag === "pj")   result = result.filter((e) => e.is_pj);
    if (filterTag === "tech") result = result.filter((e) => e.is_tech);
    result.sort((a, b) => {
      const diff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      return sortOrder === "newest" ? diff : -diff;
    });
    return result;
  }, [entries, search, filterTag, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated  = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleAddCandidate = async (data: AddCandidateFormData) => {
    setIsSubmitting(true);
    try {
      const newEntry = await addWhitelistEntry(token, data);
      setEntries((prev) => [newEntry, ...prev]);
      toast({ title: "Candidato autorizado com sucesso!", status: "success", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Erro ao adicionar e-mail", description: error.message ?? "Verifique se o e-mail já está cadastrado.", status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemove = async (email: string) => {
    setRemovingEmail(email);
    try {
      await removeWhitelistEntry(token, email);
      setEntries((prev) => prev.filter((e) => e.email !== email));
      toast({ title: "Acesso removido", status: "info", duration: 3000, isClosable: true });
    } catch (error: any) {
      toast({ title: "Erro ao remover e-mail", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setRemovingEmail(null);
    }
  };

  const handleOpenEdit = (entry: WhitelistEntry) => { setEditingEntry(entry); onEditOpen(); };

  const handleSaveEdit = async (email: string, isPj: boolean | null, isTech: boolean | null, responsibleManager: string | null) => {
    setIsSavingEdit(true);
    try {
      const updated = await patchWhitelistEntry(token, email, { is_pj: isPj, is_tech: isTech, responsible_manager: responsibleManager });
      setEntries((prev) => prev.map((e) => (e.email === email ? updated : e)));
      toast({ title: "Classificação atualizada", status: "success", duration: 3000, isClosable: true });
      onEditClose();
    } catch (error: any) {
      toast({ title: "Erro ao salvar", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleOpenSetEmail = (entry: WhitelistEntry) => { setSetEmailEntry(entry); onSetEmailOpen(); };

  const handleSaveEmail = async (ghostEmail: string, realEmail: string) => {
    setIsSavingEmail(true);
    try {
      const updated = await setWhitelistCandidateEmail(token, ghostEmail, realEmail);
      setEntries((prev) => prev.map((e) => (e.email === ghostEmail ? updated : e)));
      toast({
        title: "E-mail salvo com sucesso",
        description: "O e-mail de boas-vindas foi enviado ao candidato.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      onSetEmailClose();
    } catch (error: any) {
      toast({ title: "Erro ao salvar e-mail", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setIsSavingEmail(false);
    }
  };

  const handleResendWelcome = async (entry: WhitelistEntry) => {
    setResendingEmail(entry.email);
    try {
      const updated = await resendWelcomeEmail(token, entry.email);
      setEntries((prev) => prev.map((e) => (e.email === entry.email ? updated : e)));
      toast({
        title: "Reenvio agendado",
        description: "O e-mail de boas-vindas será reenviado em instantes.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({ title: "Erro ao reenviar e-mail", description: error.message, status: "error", duration: 4000, isClosable: true });
    } finally {
      setResendingEmail(null);
    }
  };


  return (
    <VStack spacing={5} align="stretch">

      {/* ── Header ── */}
      <HStack spacing={3}>
        <Flex align="center" justify="center" w="40px" h="40px" borderRadius="xl"
          bg={useColorModeValue("blue.50", "rgba(0,158,251,0.1)")} flexShrink={0}>
          <Icon as={ShieldCheck} boxSize={5} color="blue.500" />
        </Flex>
        <Box>
          <Text fontWeight={700} fontSize="sm">Controle de acesso</Text>
          <Text fontSize="xs" color={labelColor}>
            Apenas e-mails autorizados conseguem acessar o painel do candidato.
          </Text>
        </Box>
        <Badge ml="auto" colorScheme="blue" borderRadius="full" px={3} py={1} fontSize="xs" fontWeight={600} flexShrink={0}>
          {entries.length} {entries.length === 1 ? "autorizado" : "autorizados"}
        </Badge>
      </HStack>

      {/* ── Formulário ── */}
      <AddCandidateForm isSubmitting={isSubmitting} onSubmit={handleAddCandidate} />

      {/* ── Barra de busca e filtros ── */}
      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="sm"
      >
        {/* Cabeçalho da lista */}
        <Flex
          px={5} py={3}
          align="center"
          justify="space-between"
          borderBottom="1px solid"
          borderColor={borderColor}
          bg={sectionBg}
          flexWrap="wrap"
          gap={3}
        >
          <HStack spacing={2}>
            <Icon as={Users} boxSize={4} color={labelColor} />
            <Text fontSize="xs" fontWeight={700} color={labelColor} textTransform="uppercase" letterSpacing="0.08em">
              Candidatos autorizados
            </Text>
          </HStack>
          {!hasExternalFilters && (
            <Popover placement="bottom-end" isLazy>
              <PopoverTrigger>
                <IconButton
                  aria-label="Filtros"
                  icon={<Icon as={SlidersHorizontal} boxSize={4} />}
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  colorScheme={filterTagInternal !== "all" || sortOrderInternal !== "newest" ? "blue" : "gray"}
                />
              </PopoverTrigger>
              <PopoverContent w="200px" borderRadius="xl" boxShadow="lg" _focus={{ outline: "none" }}>
                <PopoverBody p={4}>
                  <VStack align="stretch" spacing={4}>
                    <Box>
                      <Text fontSize="10px" fontWeight={700} color={labelColor} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                        Filtros
                      </Text>
                      <RadioGroup value={filterTagInternal} onChange={(v) => setFilterTag(v as FilterTag)}>
                        <VStack align="stretch" spacing={2}>
                          <Radio value="all"  size="sm" colorScheme="blue"><Text fontSize="sm">Todos</Text></Radio>
                          <Radio value="pj"   size="sm" colorScheme="blue"><Text fontSize="sm">PJ</Text></Radio>
                          <Radio value="tech" size="sm" colorScheme="blue"><Text fontSize="sm">Tech</Text></Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                    <Divider />
                    <Box>
                      <Text fontSize="10px" fontWeight={700} color={labelColor} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
                        Ordenar por
                      </Text>
                      <RadioGroup value={sortOrderInternal} onChange={(v) => setSortOrder(v as SortOrder)}>
                        <VStack align="stretch" spacing={2}>
                          <Radio value="newest" size="sm" colorScheme="blue"><Text fontSize="sm">Mais recente</Text></Radio>
                          <Radio value="oldest" size="sm" colorScheme="blue"><Text fontSize="sm">Mais antigo</Text></Radio>
                        </VStack>
                      </RadioGroup>
                    </Box>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </Flex>

        {/* Campo de busca */}
        <Box px={5} py={3} borderBottom="1px solid" borderColor={borderColor}>
          <InputGroup size="sm">
            <InputLeftElement pointerEvents="none" pl={1}>
              <SearchIcon color="gray.400" boxSize="12px" />
            </InputLeftElement>
            <Input
              placeholder="Buscar por nome ou e-mail..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg={inputBg}
              borderRadius="lg"
              fontSize="sm"
              pl={8}
              _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
            />
          </InputGroup>
        </Box>

        {/* Lista */}
        {isLoading ? (
          <Flex justify="center" align="center" py={12}>
            <OnflyLoading containerSize={60} />
          </Flex>
        ) : filtered.length === 0 ? (
          <Flex direction="column" align="center" justify="center" py={12} gap={2}>
            <Icon as={ShieldCheck} boxSize={8} color="gray.300" />
            <Text fontSize="sm" color="gray.400" fontWeight={500}>
              {search || filterTag !== "all"
                ? "Nenhum resultado para os filtros aplicados."
                : "Nenhum candidato autorizado ainda."}
            </Text>
            {(search || filterTag !== "all") && (
              <Button size="xs" variant="ghost" colorScheme="blue"
                onClick={() => { setSearch(""); setFilterTag("all"); }}>
                Limpar filtros
              </Button>
            )}
          </Flex>
        ) : (
          <>
            <VStack spacing={0} align="stretch" divider={<Divider borderColor={borderColor} />}>
              {paginated.map((entry) => (
                <WhitelistEntryRow
                  key={entry.id}
                  entry={entry}
                  isRemoving={removingEmail === entry.email}
                  isResending={resendingEmail === entry.email}
                  onEdit={handleOpenEdit}
                  onRemove={handleRemove}
                  onResendWelcome={handleResendWelcome}
                  onSetEmail={handleOpenSetEmail}
                />
              ))}
            </VStack>

            <WhitelistPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalFiltered={filtered.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />

          </>
        )}
      </Box>

      {/* Modal de edição de classificação */}
      <EditModal
        entry={editingEntry}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onSave={handleSaveEdit}
        isSaving={isSavingEdit}
      />

      {/* Modal: informar email real */}
      <SetEmailModal
        entry={setEmailEntry}
        isOpen={isSetEmailOpen}
        onClose={onSetEmailClose}
        onSave={handleSaveEmail}
        isSaving={isSavingEmail}
      />
    </VStack>
  );
};
